import { useParams } from "react-router";
import { useMemo } from "react";
import { MODELS, useModel, useMultiModel } from "../../api";
import { Spinner } from "../../design-system/utils/Spinner/Spinner.jsx";
import NotFoundPage from "../not-found/NotFoundPage.jsx";
import { SparkStepOverviewWithData } from "../workshops/spark-step/SparkStepOverviewWithData.jsx";
import { useSparkContext } from "../workshops/context/SparkContext.jsx";

/**
 * Copy and adaptation of /workshops/spark-step/SparkStep.jsx to adjust for contexts without user/
 * where user is not a member of the workshop (demo-mode).
 *
 * SparkStep component is responsible for rendering the current step of a spark within a workshop.
 * It utilizes URL parameters to determine the current workshop, spark, and step, then fetches the relevant data to display.
 *
 * @component
 * @returns {JSX.Element} The rendered component, which includes the current step step2, navigation between steps, and additional context.
 */
const SparkStep = () => {
  const { stepSlug } = useParams();
  const { spark, structure } = useSparkContext();
  const currStep = useModel(MODELS.SPARK_SCHEDULE_STEP(spark.slug, stepSlug));
  const stageSlug = currStep.stageSlug;

  const [allCurrentStageSteps, content] = useMultiModel([
    MODELS.SPARK_SCHEDULE_STEPS(spark.slug, stageSlug),
    currStep.content?.contentKey
      ? MODELS.SPARK_MODULE_CONTENT(currStep.content.contentKey)
      : null,
  ]);

  // For the demo, only keep those steps which are not disabled and not in a disabled station.
  const currStage = useMemo(() => {
    if (!stageSlug || !structure || !Array.isArray(structure)) return null;
    return structure
      .flatMap((phase) => phase.stages)
      .find((stage) => stage.slug === stageSlug);
  }, [stageSlug, structure]);

  const hasStageSteps = allCurrentStageSteps.length > 0;
  const allCurrentStageStepsWithoutDisabledSteps = useMemo(() => {
    if (!hasStageSteps) return null;
    return allCurrentStageSteps.filter(
      (step) => !step.disabled && step.content?.station?.disabled !== true,
    );
  }, [allCurrentStageSteps, hasStageSteps]);

  const stationKey = currStep.content?.station?._key;
  const hasStageStepsWithoutDisabledSteps =
    allCurrentStageStepsWithoutDisabledSteps?.length > 0;
  const stationSteps = useMemo(() => {
    if (!stationKey || !hasStageStepsWithoutDisabledSteps) return null;
    return allCurrentStageStepsWithoutDisabledSteps.filter(
      (step) => step.content?.station?._key === stationKey,
    );
  }, [
    stationKey,
    hasStageStepsWithoutDisabledSteps,
    allCurrentStageStepsWithoutDisabledSteps,
  ]);

  if (!structure) return <Spinner />;

  if (
    currStep.disabled ||
    currStep.content?.station?.disabled ||
    currStage.disabled
  )
    return <NotFoundPage />;

  const currStepIdx = allCurrentStageStepsWithoutDisabledSteps.findIndex(
    (step) => step.slug === stepSlug,
  );
  const nextStep = allCurrentStageStepsWithoutDisabledSteps[currStepIdx + 1];
  const prevStep =
    currStepIdx > 0 &&
    allCurrentStageStepsWithoutDisabledSteps[currStepIdx - 1];

  return (
    <SparkStepOverviewWithData
      step={currStep}
      content={content}
      stationSteps={stationSteps}
      nextStep={nextStep && !nextStep.disabled ? nextStep : null}
      prevStep={prevStep && !prevStep.disabled ? prevStep : null}
      stageName={currStage?.name}
      disableSome={true}
    />
  );
};

export default SparkStep;
